import {
	ButtonSubmit,
	ColourStateSolid,
	Modal,
	ModalSize,
	UploadFile,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, FieldValues, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useState } from 'react';
import AddressImporterForm from '../AddressImporterForm';
import { AddressImporterModalType } from '../AddressImportTypes';

type AddressType = {
	name: string;
	businessName: string;
	emailAddresses: Array<any>;
	addresses: Array<any>;
	phoneNumbers: Array<any>;
};

const EmptyAddress = {
	propertyName: '',
	addressLine1: '',
	addressLine2: '',
	addressLine3: '',
	postCode: '',
	countryName: '',
	countryCode: '',
};

const AddressTypes = ['pref', 'home', 'work', 'school', 'other'];
const PhoneTypes = ['pref', 'home', 'work', 'school', 'mobile', 'cell', 'other'];
const EmailTypes = ['pref', 'home', 'work', 'school', 'internet', 'other'];

function ImportVcfModal(props: AddressImporterModalType) {
	const { t } = useTranslation();

	const [importing, setImporting] = useState<boolean>(false);
	const [parsedData, setParsedData] = useState<any>(null);

	const { handleSubmit, getValues, control } = useForm({
		defaultValues: {
			file: null,
		},
	});

	const SanitiseString = (input: string) => input.replace(/;/, '').replace(/\n/, '').replace(/\r/, '').trim();

	const ParseName = (line: string) => {
		const splitName = line.split('N:')[1].split(';');

		return `${splitName[1]} ${splitName[2] ? `${splitName[2]} ${splitName[0]}` : splitName[0]}`;
	};

	const ParseNameFallback = (line: string) => line.replace('FN:', '');

	const ParseBusinessName = (line: string) => SanitiseString(line.split('ORG:').pop() as string);

	const ParseTypes = (line: string, rowKey: string) => {
		let newData: any = line
			.replace(`${rowKey}:`, `${rowKey};`)
			.split(`${rowKey};`)[1]
			.replace(/TYPE/g, 'type')
			.split(':')[0];

		newData = newData.replace(/type#/g, '').replace(/type=/g, '').toLowerCase().split(';');

		if (typeof newData === 'string') {
			newData = newData ? [newData] : [];
		}

		return newData;
	};

	const ParseRow = (value: string, line: string, rowKey: string, matchTypes: Array<string>) => {
		if (value === '') return {};

		const entryTypes: any = ParseTypes(line, rowKey);
		const parsedEntry: Record<any, any> = {};

		if (entryTypes.length > 0) {
			for (let i = 0; i < entryTypes.length; i += 1) {
				if (entryTypes[i].indexOf(',') > -1) {
					entryTypes[i] = entryTypes[i].substring(0, entryTypes[i].indexOf(','));
				}

				if (matchTypes.includes(entryTypes[i])) {
					parsedEntry[entryTypes[i]] = value;
				}
			}
		} else {
			parsedEntry.unknown = value;
		}

		return Object.keys(parsedEntry).length > 0 ? parsedEntry : { unknown: value };
	};

	const ParseAddress = (line: string) => {
		const addressValue = line.split(':').pop() as string;

		if (addressValue === '') return {};

		const addressParts = addressValue.split(';');
		const entryTypes = ParseTypes(line, 'ADR');

		const addressEntry: Record<any, any> = {};

		let parsedPropertyNameNumber = '';
		let parsedAddressLine1 = addressParts[2];

		const [propertyNameNumberFirst, ...propertyNameNumberRest] =
			addressParts[2].indexOf(',') > -1 ? addressParts[2].split(',') : addressParts[2].split(' ');
		const possiblePropertyNameNumber = propertyNameNumberFirst.trim();

		if (possiblePropertyNameNumber) {
			parsedPropertyNameNumber = possiblePropertyNameNumber;
			parsedAddressLine1 = propertyNameNumberRest.join(' ').trim();
		}

		const UnknownAddress = {
			...addressEntry,
			propertyName: parsedPropertyNameNumber,
			addressLine1: parsedAddressLine1,
			addressLine2: addressParts[3],
			addressLine3: addressParts[4],
			postCode: addressParts[5],
			countryName: addressParts[6] ?? '',
			countryCode: addressParts[6] ?? '',
		};

		if (entryTypes.length > 0) {
			for (let i = 0; i < entryTypes.length; i += 1) {
				if (AddressTypes.includes(entryTypes[i])) {
					addressEntry[entryTypes[i]] = {
						...addressEntry,
						propertyName: parsedPropertyNameNumber,
						addressLine1: parsedAddressLine1,
						addressLine2: addressParts[3],
						addressLine3: addressParts[4],
						postCode: addressParts[5],
						countryName: addressParts[6] ?? '',
						countryCode: addressParts[6] ?? '',
					};
				}
			}
		} else {
			addressEntry.unknown = UnknownAddress;
		}

		if (Object.keys(addressEntry).length > 0) {
			return addressEntry;
		}

		return { unknown: UnknownAddress };
	};

	const ParseTelephone = (line: string) => {
		let telValue = line.split(':').pop() as string;
		telValue = telValue?.replace(/-/g, '').replace(/ /g, '').replace(/\(/g, '').replace(/\)/g, '');

		return ParseRow(telValue, line, 'TEL', PhoneTypes);
	};

	const ParseEmail = (line: string) => {
		const emailVal = line.split(':').pop() as string;

		return ParseRow(emailVal, line, 'EMAIL', EmailTypes);
	};

	const ParseRecord = (data: any): AddressType => {
		const MappedData: AddressType = {
			name: '',
			businessName: '',
			addresses: [],
			phoneNumbers: [],
			emailAddresses: [],
		};

		data.forEach((line: any) => {
			if (line.indexOf('BEGIN:') === -1 || line.indexOf('VERSION:') === -1) {
				if (line.indexOf('N:') > -1 && line.indexOf('FN:') === -1) {
					MappedData.name = ParseName(line);
				}

				if (MappedData.name.trim() === '' && line.indexOf('FN:') > -1) {
					MappedData.name = ParseNameFallback(line);
				}

				if (line.indexOf('ORG;') > -1 || line.indexOf('ORG:') > -1) {
					MappedData.businessName = ParseBusinessName(line);
				}

				if (line.indexOf('ADR;') > -1 || (line.indexOf('ADR:') > -1 && line.indexOf('ABADR') === -1)) {
					MappedData.addresses.push(ParseAddress(line));
				}

				if (line.indexOf('TEL;') > -1 || line.indexOf('TEL:') > -1) {
					MappedData.phoneNumbers.push(ParseTelephone(line));
				}

				if (line.indexOf('EMAIL:') > -1 || line.indexOf('EMAIL;') > -1) {
					MappedData.emailAddresses.push(ParseEmail(line));
				}
			}
		});

		MappedData.addresses = MappedData.addresses.filter((value) => value);
		MappedData.phoneNumbers = MappedData.phoneNumbers.filter((value) => value);
		MappedData.emailAddresses = MappedData.emailAddresses.filter((value) => value);

		return MappedData;
	};

	const ParseVcf = (input: any) => {
		const data = input
			.replace(/\r?\n|\r/g, '\n')
			.trim()
			.split('\n');

		const ParsedData: AddressType = ParseRecord(data);

		const AddressEntries: Array<any> = [];

		if (ParsedData.addresses.length > 0) {
			for (let i = 0; i < ParsedData.addresses.length; i += 1) {
				const addressType = Object.keys(ParsedData.addresses[i])[0];
				const matchedPhoneType =
					addressType && ParsedData.phoneNumbers
						? ParsedData.phoneNumbers.filter((item: string) => Object.keys(item)[0] === addressType)[0]
						: ParsedData.phoneNumbers[0];
				const matchedEmailType =
					addressType && ParsedData.emailAddresses
						? ParsedData.emailAddresses.filter((item: string) => Object.keys(item)[0] === addressType)[0]
						: ParsedData.emailAddresses[0];

				let parsedAddress = ParsedData.addresses[i][addressType];

				if (!parsedAddress) {
					parsedAddress = EmptyAddress;
				}

				AddressEntries.push({
					...parsedAddress,
					name: ParsedData.name,
					businessName: ParsedData.businessName,
					emailAddress:
						matchedEmailType && ParsedData.emailAddresses.length > 1 && Object.values(matchedEmailType)[0]
							? Object.values(matchedEmailType)[0]
							: ParsedData.emailAddresses[0]
								? Object.values(ParsedData.emailAddresses[0])[0]
								: '',
					phoneNo:
						matchedPhoneType && ParsedData.phoneNumbers.length > 1 && Object.values(matchedPhoneType)[0]
							? Object.values(matchedPhoneType)[0]
							: ParsedData.phoneNumbers[0]
								? Object.values(ParsedData.phoneNumbers[0])[0]
								: '',
				});
			}
		} else {
			AddressEntries.push({
				...EmptyAddress,
				name: ParsedData.name,
				businessName: ParsedData.businessName,
				emailAddress: ParsedData.emailAddresses[0] ? Object.values(ParsedData.emailAddresses[0])[0] : '',
				phoneNo: ParsedData.phoneNumbers[0] ? Object.values(ParsedData.phoneNumbers[0])[0] : '',
			});
		}

		setImporting(false);

		if (props.forwardData && props.actionedCallback) {
			props.actionedCallback(AddressEntries);
		} else {
			setParsedData(AddressEntries);
		}
	};

	const ReadLocalFile = (data: any) => {
		const reader = new FileReader();

		reader.onload = () => {
			ParseVcf(reader.result);
		};

		reader.readAsBinaryString(data.file[0]);
	};

	const HandleImport = (data: FieldValues, e: any) => {
		setImporting(true);
		ReadLocalFile(data);
	};

	const HandleSave = (data: any) => {
		if (props.actionedCallback) {
			props.actionedCallback(data);
		}
	};

	return (
		<Modal
			title={t('address.vcf.title')}
			isOpen={props.isOpen}
			closeCallback={() => props.closeCallback()}
			modalSize={ModalSize.ExtraLarge}
		>
			{!parsedData && (
				<form onSubmit={handleSubmit((data, e) => HandleImport(data, e))}>
					<fieldset className='flex-column flex-gap-md'>
						<Controller
							control={control}
							name='file'
							rules={{ required: t('address.vcf.validation.noFile') }}
							render={({ field }) => (
								<UploadFile
									name='VCF File Upload'
									onError={(e) => null}
									onChange={(e: any) => field.onChange(e)}
									acceptsFileExtension={['.vcf']}
									extensionDisplayValue='VCF'
									maxFiles={1}
								/>
							)}
						/>

						<ButtonSubmit
							loading={importing}
							disabled={importing}
							title={t('address.vcf.actions.import')}
							fullWidth
							colour={ColourStateSolid.Primary}
						/>
					</fieldset>
				</form>
			)}

			{parsedData && (
				<AddressImporterForm
					addressData={parsedData}
					embeddedForm
					savedCallback={(data: any) => HandleSave(data)}
					removeCallback={() => setParsedData(null)}
				/>
			)}
		</Modal>
	);
}

export default ImportVcfModal;
